import type { ChildProfile } from "@howcomet/shared";
import { AVATARS } from "@howcomet/shared";

/**
 * What a child sees when the family's subscription has lapsed.
 *
 * This is the paywall as a child meets it, and it is a different screen from
 * the one the grown-up meets. A child cannot fix this, cannot be told a price,
 * and must not be handed anything that reads as their fault or as a thing to go
 * and press a parent about. So: nothing has broken, nothing is lost, and there
 * is exactly one thing to do — fetch a grown-up.
 *
 * It replaced a bare line of grey text sitting under the top bar, which is what
 * the three tabs each independently rendered when their fetch came back 403.
 * The state is real and the copy was right; it just looked like an error page
 * for a machine.
 *
 * The count of what they already have is the point of the whole screen. A dark
 * empty page after a lapse reads as "your stars are gone" to an eight-year-old,
 * and the fear that the sky is deleted is the one this has to answer first.
 */
export function RestingScreen({
  profile,
  message,
  onGrownUps,
  onSignOut,
}: {
  profile: ChildProfile;
  /** The server's own words. It knows why it refused; we only know it did. */
  message: string;
  onGrownUps: () => void;
  onSignOut: () => void;
}) {
  const avatar = AVATARS.find((a) => a.id === profile.avatarId)?.emoji ?? "⭐";

  return (
    <div className="safe-top flex min-h-dvh flex-col items-center justify-center px-6 py-10">
      <div className="rise w-full max-w-md text-center">
        {/*
          The comet asleep rather than an error glyph. Its glow is a real
          element behind it, not a shadow on the emoji, so it can breathe on its
          own slow cycle — the screen has to feel like a nap and not a stop.
        */}
        <div className="relative mx-auto h-[140px] w-[140px]">
          <div className="twinkle absolute inset-0 rounded-full bg-lilac/25 blur-2xl" />
          <div className="twinkle absolute inset-6 rounded-full bg-gold/20 blur-xl" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="drowse text-[76px]" role="img" aria-label="Howcomet, asleep">
              ☄️
            </span>
          </div>
          {/* Zeds grow as they rise, which is the only way round that reads as
              breathing out rather than as two stray letters. */}
          <span
            aria-hidden
            className="drowse absolute right-1 top-6 text-[15px] text-lilac/60"
            style={{ animationDelay: "1.6s" }}
          >
            z
          </span>
          <span
            aria-hidden
            className="drowse absolute right-[-2px] top-0 text-[24px] text-lilac/75"
            style={{ animationDelay: "0.8s" }}
          >
            z
          </span>
        </div>

        {/*
          The server's own words, at headline size, rather than a heading of
          ours with the server's sentence repeated underneath it — which is what
          this was, and it read as the app saying the same thing twice because
          it was.

          It knows why it refused; we only know that it did. If that wording
          ever changes, this changes with it.
        */}
        <h1 className="disp mx-auto mt-5 max-w-md text-[26px] leading-snug text-cream">
          {message}
        </h1>

        {/*
          Their sky, still there and still counted. Shown as the things
          themselves rather than as a sentence, because the child this reassures
          is often the one who cannot yet read the sentence.
        */}
        <div className="mt-7 rounded-3xl bg-panel p-5">
          <p className="disp text-[13px] uppercase tracking-wider text-cream/45">
            Safe while you wait
          </p>
          <div className="mt-3 flex items-stretch gap-3">
            <div className="flex-1 rounded-2xl bg-panel2 px-3 py-4">
              <div className="disp text-[26px] text-gold">⭐ {profile.stardust}</div>
              <div className="mt-1 text-[14px] text-cream/60">stardust</div>
            </div>
            <div className="flex-1 rounded-2xl bg-panel2 px-3 py-4">
              <div className="disp text-[26px] text-cream">🌟 {profile.starCount}</div>
              <div className="mt-1 text-[14px] text-cream/60">
                {profile.starCount === 1 ? "star" : "stars"}
              </div>
            </div>
          </div>
          <p className="mt-3 text-[15px] leading-relaxed text-cream/60">
            Nothing is lost, {profile.name}. Your sky is waiting exactly where you left it.
          </p>
        </div>

        {/*
          The only way out of here, and it goes to the grown-up's gate — the
          password screen, and the price behind it. A child cannot buy anything
          by pressing this and there is nothing on the other side of it they can
          get past, which is why it can be the big gold button.
        */}
        <button
          onClick={onGrownUps}
          className="kidbtn disp mt-6 w-full rounded-3xl bg-gold py-4 text-[19px] text-night shadow-[0_6px_0_rgba(0,0,0,.35)]"
        >
          Show this to a grown-up
        </button>

        <p className="mt-4 text-[14px] leading-relaxed text-cream/45">
          Signed in as{" "}
          <span className="text-cream/70">
            {avatar}&nbsp;{profile.name}
          </span>
        </p>

        {/* The ordinary case on a shared tablet: it is a sibling's turn, and
            their family is not the one that lapsed. Costs a pin, not a sign-up —
            the handle stays remembered. */}
        <button onClick={onSignOut} className="kidbtn mt-2 w-full py-2 text-[14px] text-cream/40">
          Sign in as someone else
        </button>
      </div>
    </div>
  );
}
